import { useState, useEffect, useRef, useCallback } from 'react';
import { useTetris } from '@/game/useTetris';
import { TetrisBoard } from '@/components/TetrisBoard';
import { NextPieceDisplay } from '@/components/NextPieceDisplay';
import { ScorePanel } from '@/components/ScorePanel';
import { MobileControls } from '@/components/MobileControls';
import { GameOverlay } from '@/components/GameOverlay';
import { NewRecordModal } from '@/components/NewRecordModal';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { NewsletterForm } from '@/components/NewsletterForm';
import { GameMascot } from '@/components/GameMascot';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

const Index = () => {
  const { t, language } = useLanguage();
  const game = useTetris();
  const [highScore, setHighScore] = useState(0);
  const [highScoreDate, setHighScoreDate] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [newRecordData, setNewRecordData] = useState<{ score: number; couponCode: string } | null>(null);
  const prevGameState = useRef(game.gameState);

  // Fetch high score
  const fetchHighScore = useCallback(async () => {
    try {
      const { data } = await supabase.functions.invoke('get-highscore');
      if (data?.highScore) {
        setHighScore(data.highScore);
        setHighScoreDate(data.timestamp ?? null);
      }
    } catch (e) {
      console.error('Failed to fetch high score:', e);
    }
  }, []);


  useEffect(() => { fetchHighScore(); }, [fetchHighScore]);

  // Create session on game start
  const createSession = useCallback(async () => {
    try {
      const { data } = await supabase.functions.invoke('create-session');
      if (data) {
        setSessionId(data.sessionId);
        setSessionToken(data.sessionToken);
      }
    } catch (e) {
      console.error('Failed to create session:', e);
    }
  }, []);

  // Submit score on game over
  useEffect(() => {
    if (prevGameState.current === 'playing' && game.gameState === 'gameover' && sessionId && sessionToken) {
      const submitScore = async () => {
        try {
          const { data } = await supabase.functions.invoke('submit-score', {
            body: { sessionId, sessionToken, score: game.score, language },
          });
          if (data?.isNewRecord && data.couponCode) {
            setNewRecordData({ score: data.newHighScore, couponCode: data.couponCode });
            setHighScore(data.newHighScore);
            setHighScoreDate(new Date().toISOString());
          }
        } catch (e) {
          console.error('Failed to submit score:', e);
        }
      };
      submitScore();
    }
    prevGameState.current = game.gameState;
  }, [game.gameState, game.score, sessionId, sessionToken]);

  const handleStart = useCallback(() => {
    setNewRecordData(null);
    createSession();
    game.startGame();
  }, [createSession, game]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <Navigation />

      {/* Spacer for fixed nav */}
      <div className="h-36" />

      {/* Game title */}
      <div className="text-center py-4 md:py-6">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold gradient-text tracking-tight">
          Haiko-Tetris
        </h1>
        <p className="text-sm md:text-base text-muted-foreground mt-1">{t.game.playAndWin}</p>
      </div>

      {/* Main game area */}
      <main className="flex-1 flex items-start justify-center px-4 py-6 gap-6 lg:gap-10">
        {/* Left panel: score + controls */}
        <div className="hidden md:flex flex-col gap-4 w-52 lg:w-56 pt-2 shrink-0">
          <ScorePanel score={game.score} level={game.level} lines={game.lines} highScore={highScore} highScoreDate={highScoreDate} />
          <NextPieceDisplay piece={game.nextPiece} />
          <div className="glass-card p-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-accent">←→</span> {t.controls.move} &nbsp;
              <span className="font-semibold text-accent">↑</span> {t.controls.rotate} &nbsp;
              <span className="font-semibold text-accent">↓</span> {t.controls.down} &nbsp;
              <span className="font-semibold text-accent">Space</span> {t.controls.drop} &nbsp;
              <span className="font-semibold text-accent">P</span> {t.controls.pause}
            </p>
          </div>
        </div>

        {/* Center: game board */}
        <div className="relative w-full max-w-[280px] md:max-w-[340px] lg:max-w-[380px] shrink-0">
          <GameOverlay gameState={game.gameState} score={game.score} onStart={handleStart} />
          <TetrisBoard board={game.board} />
          
          {/* Mobile score bar */}
          <div className="flex justify-between items-center mt-2 md:hidden text-xs text-muted-foreground">
            <span>{t.mobileScore.score}: <strong className="text-foreground">{game.score}</strong></span>
            <span>{t.mobileScore.level}: <strong className="text-foreground">{game.level}</strong></span>
            <span>{t.mobileScore.record}: <strong className="text-accent">{highScore}</strong></span>
          </div>
          
          <MobileControls
            onLeft={game.moveLeft}
            onRight={game.moveRight}
            onDown={game.moveDown}
            onRotate={game.rotate}
            onHardDrop={game.hardDrop}
          />
        </div>

        {/* Right panel: mascot + CTA */}
        <div className="hidden lg:flex flex-col gap-4 w-56 shrink-0">
          <div className="relative flex justify-center pt-12">
            <GameMascot
              gameState={game.gameState}
              score={game.score}
              lines={game.lines}
              level={game.level}
              highScore={highScore}
            />
          </div>
          <div className="mt-4 glass-card p-3 text-center">
            <p className="text-sm font-bold text-foreground mb-1">{t.cta.title}</p>
            <p className="text-xs text-muted-foreground leading-snug">
              {t.cta.description} <span className="font-bold text-accent">{t.cta.discount}</span> {t.cta.suffix}
            </p>
          </div>
          <div className="glass-card p-3 text-xs text-muted-foreground space-y-0.5">
            <p className="font-semibold text-foreground">{t.cta.termsTitle}</p>
            {t.cta.terms.map((term, i) => (
              <p key={i}>• {term}</p>
            ))}
          </div>
        </div>
      </main>

      {/* Spacer before footer */}
      <div className="h-16 md:h-24 lg:h-32" />

      {/* Newsletter above footer */}
      <NewsletterForm />

      {/* Footer */}
      <Footer />

      {/* New record modal */}
      {newRecordData && (
        <NewRecordModal
          score={newRecordData.score}
          couponCode={newRecordData.couponCode}
          onClose={() => setNewRecordData(null)}
        />
      )}
    </div>
  );
};

export default Index;
